/* Kâr Planlayıcı (Dene-Yanıl) — gerçek fee/bonus matematiğiyle PnL projeksiyonu */
import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Target, AlertTriangle, Rocket, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";
import { fmtSignedUsd, colorForPnl } from "@/lib/format";

const defaults = {
  balance_usd: 500,
  leverage: 10,
  daily_target_usd: 20,
  price_move_pct: 3,
  funding_per_trade_usd: 0,
};

export default function ProfitPlanner() {
  const [form, setForm] = useState(defaults);
  const [plan, setPlan] = useState(null);
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: api.getSettings });

  const projMut = useMutation({
    mutationFn: api.planProjection,
    onSuccess: setPlan,
    onError: (e) => toast.error(errMsg(e)),
  });
  const execMut = useMutation({
    mutationFn: api.planExecute,
    onSuccess: (d) => {
      if (d.hedge_tpsl_error) {
        toast.warning(`Pozisyon açıldı ama TP/SL kurulamadı: ${d.hedge_tpsl_error}`);
      } else {
        toast.success(`Tur başladı ✅ Pozisyon açık · Hedge TP/SL tetik $${d.trigger_price}`);
      }
    },
    onError: (e) => toast.error(errMsg(e)),
  });
  const targetMut = useMutation({
    mutationFn: api.planSetDailyTarget,
    onSuccess: (d) =>
      toast.success(`Günlük hedef kaydedildi: $${d.daily_target_usd} — Panel'de ilerleme çubuğu aktif 🎯`),
    onError: (e) => toast.error(errMsg(e)),
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const run = () =>
    projMut.mutate({
      balance_usd: Number(form.balance_usd),
      leverage: Number(form.leverage),
      daily_target_usd: Number(form.daily_target_usd),
      price_move_pct: Number(form.price_move_pct),
      funding_per_trade_usd: Number(form.funding_per_trade_usd) || 0,
    });

  const [armed, setArmed] = useState(false);
  const execute = () => {
    if (!armed) {
      setArmed(true);
      setTimeout(() => setArmed(false), 4000);
      return;
    }
    setArmed(false);
    execMut.mutate({
      symbol: settings?.signal_symbol || "ETHUSDT",
      counter_exchange: "BINANCE",
      notional_usd: plan.plan.suggested_notional_usd,
      leverage: Number(form.leverage),
      price_move_pct: Number(form.price_move_pct),
    });
  };

  const inputCls = "bg-transparent border-[#27272A] h-9 rounded-sm font-mono";

  return (
    <div className="bg-[#121212] border border-[#ff5d15]/30 rounded-md" data-testid="profit-planner">
      <div className="px-4 py-3 border-b border-[#1A1A1A] flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Target className="w-4 h-4 text-[#ff5d15]" />
          <h2 className="text-sm font-semibold">Kâr Planlayıcı · Dene-Yanıl</h2>
        </div>
        {plan && (
          <span className="text-[10px] font-mono text-zinc-500">
            Tier <b className="text-[#ff5d15]">{plan.tier}</b> · bonus %{plan.bonus_rate_pct} ·
            tur ücreti %{plan.fees_pct_roundtrip} — gerçek değerler
          </span>
        )}
      </div>

      <div className="p-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Field label="Bakiye ($)">
            <Input data-testid="plan-balance" type="number" value={form.balance_usd}
                   onChange={set("balance_usd")} className={inputCls} />
          </Field>
          <Field label="Kaldıraç (×)">
            <Input data-testid="plan-leverage" type="number" value={form.leverage}
                   onChange={set("leverage")} className={inputCls} />
          </Field>
          <Field label="Günlük Hedef Kâr ($)">
            <Input data-testid="plan-target" type="number" value={form.daily_target_usd}
                   onChange={set("daily_target_usd")} className={inputCls} />
          </Field>
          <Field label="Fiyat Hareketi (%)">
            <Input data-testid="plan-move" type="number" step="0.1" value={form.price_move_pct}
                   onChange={set("price_move_pct")} className={inputCls} />
          </Field>
          <Field label="Funding/İşlem ($)">
            <Input data-testid="plan-funding" type="number" step="0.1"
                   value={form.funding_per_trade_usd}
                   onChange={set("funding_per_trade_usd")} className={inputCls} />
          </Field>
        </div>
        <Button data-testid="plan-run" onClick={run} disabled={projMut.isPending}
                className="mt-3 w-full bg-[#ff5d15] hover:bg-[#f0b90b] text-black rounded-sm h-9 font-semibold">
          {projMut.isPending ? "Hesaplanıyor…" : "Planı Hesapla"}
        </Button>

        {plan && (
          <div className="mt-4 space-y-4">
            {plan.warnings.length > 0 && (
              <div className="border border-[#f0b90b]/40 bg-[#f0b90b]/5 rounded-sm p-3 space-y-1"
                   data-testid="plan-warnings">
                {plan.warnings.map((w, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs text-[#f0b90b]">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />{w}
                  </div>
                ))}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* İşlem başına matematik */}
              <div className="border border-[#27272A] rounded-sm p-3">
                <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-2">
                  İşlem Başına Gerçek Matematik
                </div>
                <Row l="Pozisyon boyutu" v={`$${plan.per_trade.notional_usd}`} />
                <Row l="Gerekli marj (2 bacak)" v={`$${plan.plan.margin_required_usd}`} />
                <Row l="📉 Darkex zararı (realize)" v={`-$${plan.per_trade.darkex_loss}`} c="text-[#ff5662]" />
                <Row l="📈 Binance kârı" v={`+$${plan.per_trade.counter_gain}`} c="text-[#2bd98a]" />
                <Row l={`🎁 Bonus (%${plan.bonus_rate_pct})`} v={`+$${plan.per_trade.bonus}`} c="text-[#ff5d15]" />
                <Row l="💸 Ücretler (4 bacak)" v={`-$${plan.per_trade.fees}`} c="text-zinc-400" />
                {plan.per_trade.funding > 0 && (
                  <Row l="Funding" v={`-$${plan.per_trade.funding}`} c="text-zinc-400" />
                )}
                <hr className="border-[#1A1A1A] my-2" />
                <Row l="NET / işlem" v={fmtSignedUsd(plan.per_trade.net)}
                     c={`${colorForPnl(plan.per_trade.net)} text-base font-bold`} testId="plan-net-per-trade" />
                {plan.pro3_comparison_net > plan.per_trade.net && (
                  <div className="text-[10px] text-zinc-500 mt-1">
                    PRO3 (%35) olsaydın: <b className="text-[#2bd98a]">{fmtSignedUsd(plan.pro3_comparison_net)}</b>/işlem
                  </div>
                )}
              </div>

              {/* Günlük plan */}
              <div className="border border-[#27272A] rounded-sm p-3">
                <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-2">
                  Günlük Plan · hedef ${plan.plan.daily_target_usd}
                </div>
                <div className="text-3xl font-mono font-bold mt-1" data-testid="plan-trades-needed">
                  {plan.plan.trades_per_day_needed > 0 ? (
                    <>
                      <span className="text-[#ff5d15]">{plan.plan.trades_per_day_needed}</span>
                      <span className="text-sm text-zinc-500"> tur/gün</span>
                    </>
                  ) : (
                    <span className="text-[#ff5662] text-lg">Hedefe ulaşılamaz</span>
                  )}
                </div>
                <div className="mt-2 space-y-1">
                  <Row l="Tahmini günlük net" v={fmtSignedUsd(plan.plan.daily_net)}
                       c={colorForPnl(plan.plan.daily_net)} />
                  <Row l="Tahmini aktif süre" v={`~${plan.plan.estimated_hours_per_day} saat/gün`} />
                  <Row l="Tur başına süre" v={`~${plan.plan.minutes_per_trade} dk (5 dk zorunlu tutma dahil)`} />
                </div>
                <Button onClick={() => targetMut.mutate(Number(form.daily_target_usd))}
                        disabled={targetMut.isPending}
                        data-testid="plan-save-target-btn"
                        className="mt-3 w-full h-8 rounded-sm text-xs bg-[#ff5d15]/10 text-[#ff5d15] border border-[#ff5d15]/40 hover:bg-[#ff5d15]/20">
                  {targetMut.isPending ? "Kaydediliyor…"
                    : `🎯 Hedefi Kaydet ($${form.daily_target_usd}/gün) — Panel'de takip et`}
                </Button>
                {plan.plan.target_achievable && plan.per_trade.net > 0 && (
                  <Button onClick={execute} disabled={execMut.isPending}
                          data-testid="plan-execute-btn"
                          className={`mt-3 w-full h-9 rounded-sm font-semibold text-xs ${
                            armed ? "bg-[#ff5662] text-white" : "bg-[#2bd98a]/15 text-[#2bd98a] border border-[#2bd98a]/40 hover:bg-[#2bd98a]/25"
                          }`}>
                    <Rocket className="w-3.5 h-3.5 mr-1" />
                    {execMut.isPending ? "Gönderiliyor…"
                      : armed ? "EMİN MİSİN? Gerçek emir gider — tekrar tıkla"
                      : `Bu planla işleme gir (1 tur · $${plan.plan.suggested_notional_usd})`}
                  </Button>
                )}
              </div>
            </div>

            {/* Projeksiyon tablosu */}
            <div className="border border-[#27272A] rounded-sm overflow-x-auto">
              <div className="px-3 py-2 border-b border-[#1A1A1A] flex items-center gap-2">
                <TrendingUp className="w-3.5 h-3.5 text-[#ff5d15]" />
                <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                  PnL Projeksiyonu — Sabit vs Bileşik
                </span>
              </div>
              <table className="w-full text-sm min-w-[520px]" data-testid="plan-projection-table">
                <thead className="text-[10px] uppercase tracking-wider text-zinc-500 bg-[#0F0F0F]">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Dönem</th>
                    <th className="px-3 py-2 text-right font-medium">Sabit Kâr</th>
                    <th className="px-3 py-2 text-right font-medium">Bileşik Kâr</th>
                    <th className="px-3 py-2 text-right font-medium">Bileşik Bakiye</th>
                  </tr>
                </thead>
                <tbody className="font-mono text-xs">
                  {plan.projections.map((p) => (
                    <tr key={p.period} className="border-t border-[#1A1A1A]"
                        data-testid={`plan-proj-${p.days}`}>
                      <td className="px-3 py-2 text-zinc-300">{p.period}</td>
                      <td className={`px-3 py-2 text-right ${colorForPnl(p.fixed_profit)}`}>
                        {fmtSignedUsd(p.fixed_profit)}
                      </td>
                      <td className={`px-3 py-2 text-right ${colorForPnl(p.compound_profit)}`}>
                        {fmtSignedUsd(p.compound_profit)}
                      </td>
                      <td className="px-3 py-2 text-right text-zinc-300">
                        ${p.compound_balance.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">
      {label}
    </Label>
    {children}
  </div>
);

const Row = ({ l, v, c = "text-zinc-300", testId }) => (
  <div className="flex justify-between items-baseline text-sm py-0.5">
    <span className="text-xs text-zinc-500">{l}</span>
    <span className={`font-mono ${c}`} data-testid={testId}>{v}</span>
  </div>
);
